import { useState } from "react";
import { guid } from "odata-query";
import { homeService, taskService } from "@miplatform/api";
import { DETAILS_TAB, HTTP_STATUSES, ORDER_TYPES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IDocumentListItemResponse,
  IHome,
  IMilestone,
  IOrderTemplateResponse,
  IWorkOrder,
  IWorkOrderTemplate,
} from "@miplatform/types";
import { bugsnag, sortOrdersByStatus } from "@miplatform/utils";
import { useInspectionAttempts } from "../../hooks";

const getFilters = (key: string, value: string) => {
  return {
    [key]: { type: "guid", value },
  };
};

const PROFILE_PICTURE_FILE_NAME = "profile-picture";

const parseExternalData = (externalData: any) => {
  try {
    if (!externalData) {
      return { orderTypeName: "", tradeName: "" };
    }
    return JSON.parse(decodeURIComponent(externalData));
  } catch (error: any) {
    bugsnag.notify(error);
    return { orderTypeName: "", tradeName: "" };
  }
};

export const useHomeDetailsScreenViewModel = (environment: string) => {
  const [home, setHome] = useState<IHome>();
  const [activeTab, setActiveTab] = useState(DETAILS_TAB);
  const [documents, setDocuments] = useState<IDocumentListItemResponse[]>([]);
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentListItemResponse | null>(null);
  const [orders, setOrders] = useState<IWorkOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<IWorkOrder | null>(null);
  const [isFetchingOrderDetails, setIsFetchingOrderDetails] = useState(false);
  const [milestones, setMilestones] = useState<IMilestone[]>([]);
  const [contacts, setContacts] = useState([]);
  const [community, setCommunity] = useState(null);
  const [issues, setIssues] = useState([]);
  const [orderTemplateOptions, setOrderTemplateOptions] = useState<
    IWorkOrderTemplate[]
  >([]);
  const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState("");
  const [communities, setCommunities] = useState([]);

  const stateManager = useComponentState();

  const {
    getInspections: fetchInspections,
    inspections,
    setInspections,
  } = useInspectionAttempts();

  const getInspections = async (homeId: string) => {
    const homeFilter = { home: { id: guid(homeId) } };
    const { data: inspections } = await fetchInspections({
      filters: homeFilter,
    });
    setInspections(inspections);
  };

  const getHome = async (id: string) => {
    stateManager.run(async () => {
      const response = await homeService.homes.getById({ id });
      if (response.status === HTTP_STATUSES.OK) {
        const { data } = response;

        setHome({
          ...data,
          addressLine1: data.address?.addressLine1,
          addressLine2: data.address?.addressLine2,
          city: data.address?.city,
          county: data.address?.county,
          latitude: data.address?.location?.latitude,
          longitude: data.address?.location?.longitude,
          postalCode: data.address?.postalCode,
          regionCode: data.address?.regionCode,
        });

        setDocuments(
          data.documents
            .filter(
              (document: IDocumentListItemResponse) =>
                document.metadata_Title !== PROFILE_PICTURE_FILE_NAME
            )
            .map((document: IDocumentListItemResponse) => ({
              ...document,
              name: document.metadata_Title || document.filename,
              ...parseExternalData(document?.externalData),
            }))
        );
        setContacts(data.contacts);
      }
    });
  };

  const updateHome = async (id: string, data: IHome) => {
    stateManager.run(async () => {
      const { data: responseData, status } = await homeService.homes.update({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: "Home updated successfully.",
          },
        },
      });
      if (status === HTTP_STATUSES.OK) {
        setHome({
          ...responseData,
          addressLine1: responseData.address?.addressLine1,
          addressLine2: responseData.address?.addressLine2,
          city: responseData.address?.city,
          county: responseData.address?.county,
          latitude: responseData.address?.location?.latitude,
          longitude: responseData.address?.location?.longitude,
          postalCode: responseData.address?.postalCode,
          regionCode: responseData.address?.regionCode,
        });
      }
    });
  };

  const getOrders = async (id: string): Promise<IWorkOrder[]> => {
    return stateManager.run(async () => {
      const response = await taskService.order.getAll({
        filters: getFilters("homeId", id),
      });
      if (response.status === HTTP_STATUSES.OK) {
        const sortedOrders = sortOrdersByStatus(response.data);
        setOrders(sortedOrders);
        return sortedOrders;
      }
      return [];
    });
  };

  const getOrderActivity = async (orderId: string) => {
    return stateManager.run(async () => {
      setIsFetchingOrderDetails(true);
      const response = await taskService.activities.getAll({
        params: {
          refId: `task-service/orders/${orderId}`,
        },
      });
      setIsFetchingOrderDetails(false);
      return response;
    });
  };

  const getMilestones = async () => {
    return stateManager.run(async () => {
      const response = await taskService.milestones.getAll({
        sort: "name asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setMilestones(
          response.data.map((milestone: IMilestone, index: number) => ({
            ...milestone,
            displayName: `MS${(index + 1).toString().padStart(2, "0")}: ${
              milestone.name
            }`,
            data: milestone.orderTemplates || [],
          }))
        );
      }
    });
  };

  const getOrderTemplates = () => {
    stateManager.run(async () => {
      const response = await taskService.orderTemplates.getAll({
        filters: {
          homePhases: { any: ORDER_TYPES.UNDER_CONSTRUCTION },
          fulfillmentSequence: { ne: null },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderTemplateOptions(
          response.data
            .sort(
              (a: IOrderTemplateResponse, b: IOrderTemplateResponse) =>
                (a?.fulfillmentSequence ?? 0) - (b?.fulfillmentSequence ?? 0)
            )
            .map(({ id, name, shortName, color }: IOrderTemplateResponse) => ({
              value: id,
              label: name,
              shortName,
              backgroundColor: color,
            }))
        );
      }
    });
  };

  const getCommunity = async (communityId: string) => {
    stateManager.run(async () => {
      const response = await homeService.communities.getById({
        id: communityId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCommunity(response.data);
      }
    });
  };

  const getCommunities = (builderId: string) => {
    stateManager.run(async () => {
      const response = await homeService.communities.getAll({
        sort: "name",
        filters: {
          builder: { id: { eq: { type: "guid", value: builderId } } },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCommunities(response.data);
      }
    });
  };

  const getHomeIssues = async (homeId: string) => {
    stateManager.run(async () => {
      const response = await taskService.issues.getAll({
        filters: { homeId: { eq: { type: "guid", value: homeId } } },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setIssues(response.data);
      }
    });
  };

  const handleRefresh = async (homeId: string) => {
    const currentOrders = await getOrders(homeId);
    if (selectedOrder && currentOrders) {
      const index = currentOrders.findIndex((x) => x.id === selectedOrder.id);
      if (index !== -1) {
        setSelectedOrder({ ...currentOrders[index] });
      }
    }
  };

  return {
    home,
    community,
    isLoading: stateManager.isBusy(),
    activeTab,
    contacts,
    documents,
    orders,
    selectedOrder,
    selectedDocument,
    isFetchingOrderDetails,
    milestones,
    issues,
    orderTemplateOptions,
    lastFetchedTimestamp,
    communities,
    inspections,
    setActiveTab,
    setSelectedOrder,
    setSelectedDocument,
    setLastFetchedTimestamp,
    getCommunity,
    getCommunities,
    getHome,
    getInspections,
    getOrders,
    getOrderActivity,
    getMilestones,
    getHomeIssues,
    getOrderTemplates,
    updateHome,
    handleRefresh,
  };
};
